import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'wouter';
import { Library, ListMusic, Music2, ChevronRight, MessageSquare } from 'lucide-react';
import { Layout } from '../components/Layout';
import { api } from '../lib/api';

export function BandHome() {
  const params = useParams<{ bandId: string }>();
  const bandId = Number(params.bandId);

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const { data: songs = [] } = useQuery({
    queryKey: ['songs', bandId],
    queryFn: () => api.songs.list(bandId),
  });

  const { data: setlists = [] } = useQuery({
    queryKey: ['setlists', bandId],
    queryFn: () => api.setlists.list(bandId),
  });

  const { data: notes = [] } = useQuery({
    queryKey: ['rehearsalNotes', bandId],
    queryFn: () => api.rehearsalNotes.listForBand(bandId),
  });

  return (
    <Layout bandId={bandId} bandName={band?.name} title="Home">
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6">
        <div>
          <h2 className="text-xl font-bold mb-1">{band?.name || 'Loading...'}</h2>
          {band?.description && <p className="text-sm text-gray-400">{band.description}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link href={`/bands/${bandId}/songs`}>
            <div className="bg-surface-900 border border-surface-700 rounded-xl p-5 hover:border-primary-500/50 transition-colors cursor-pointer group">
              <div className="flex items-center justify-between mb-3">
                <Library className="w-6 h-6 text-primary-400" />
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-primary-400" />
              </div>
              <h3 className="font-semibold mb-1">Song Library</h3>
              <p className="text-sm text-gray-400">{songs.length} song{songs.length !== 1 ? 's' : ''}</p>
            </div>
          </Link>

          <Link href={`/bands/${bandId}/setlists`}>
            <div className="bg-surface-900 border border-surface-700 rounded-xl p-5 hover:border-primary-500/50 transition-colors cursor-pointer group">
              <div className="flex items-center justify-between mb-3">
                <ListMusic className="w-6 h-6 text-accent-400" />
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-accent-400" />
              </div>
              <h3 className="font-semibold mb-1">Setlists</h3>
              <p className="text-sm text-gray-400">{setlists.length} setlist{setlists.length !== 1 ? 's' : ''}</p>
            </div>
          </Link>

          <Link href={`/bands/${bandId}/notes`}>
            <div className="bg-surface-900 border border-surface-700 rounded-xl p-5 hover:border-primary-500/50 transition-colors cursor-pointer group">
              <div className="flex items-center justify-between mb-3">
                <MessageSquare className="w-6 h-6 text-emerald-400" />
                <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-emerald-400" />
              </div>
              <h3 className="font-semibold mb-1">Rehearsal Notes</h3>
              <p className="text-sm text-gray-400">{notes.length} note{notes.length !== 1 ? 's' : ''}</p>
            </div>
          </Link>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-300">Recent Songs</h3>
            <Link href={`/bands/${bandId}/songs`}>
              <span className="text-xs text-primary-400 hover:text-primary-300 cursor-pointer">View All</span>
            </Link>
          </div>
          {songs.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No songs yet. Head to the Song Library to add some.</p>
          ) : (
            <div className="space-y-1">
              {songs.slice(0, 5).map((song: any) => (
                <Link key={song.id} href={`/bands/${bandId}/songs/${song.id}`}>
                  <div className="flex items-center gap-3 p-3 bg-surface-900 border border-surface-700 rounded-lg hover:border-surface-200/20 transition-colors cursor-pointer">
                    <Music2 className="w-4 h-4 text-gray-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{song.title}</p>
                      {song.artist && <p className="text-xs text-gray-500 truncate">{song.artist}</p>}
                    </div>
                    {song.defaultKey && <span className="text-xs bg-surface-800 text-gray-400 px-2 py-0.5 rounded">{song.defaultKey}</span>}
                    {song.defaultTempo && <span className="text-xs bg-surface-800 text-gray-400 px-2 py-0.5 rounded">{song.defaultTempo} BPM</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
